import exifr from "exifr";

export interface ExifResult {
  exif_metadata: Record<string, any> | null;
  // GeoJSON Point sent as `geometry` on POST — matches the shape every other geo entity uses
  geometry: { type: "Point"; coordinates: [number, number] } | null;
}

const IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/tiff",
  "image/png",
  "image/heic",
  "image/heif",
  "image/webp",
  "image/avif"
]);

/**
 * Strip null bytes and ASCII control characters (0x00–0x1F, 0x7F) from a string.
 * Postgres jsonb rejects strings containing null bytes; stripping all control
 * characters is safe for EXIF data (they should never legitimately appear there).
 * Returns null if the string is empty after stripping so callers can omit the key.
 */
function sanitizeString(value: string): string | null {
  const cleaned = value.replace(new RegExp("[\u0000-\u001f\u007f]", "g"), "").trim();
  return cleaned.length > 0 ? cleaned : null;
}

/**
 * Recursively remove binary / non-serializable / Postgres-unsafe values from
 * raw EXIF output. Specifically:
 *   - Uint8Array / ArrayBuffer / DataView → dropped (binary blobs)
 *   - Strings containing null bytes or control chars → stripped; dropped if empty
 *   - Arrays of objects (raw rational/IFD structs) → dropped
 *   - Dates → converted to ISO string
 *   - Nested objects → recursed; empty results dropped
 *   - Numbers and booleans → kept as-is
 */
function sanitizeExif(raw: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(raw)) {
    if (value === undefined || value === null) continue;

    // Drop binary blobs — not serializable to jsonb and not useful
    if (value instanceof Uint8Array || value instanceof ArrayBuffer || value instanceof DataView) continue;

    if (typeof value === "string") {
      const cleaned = sanitizeString(value);
      if (cleaned !== null) result[key] = cleaned;
      continue;
    }

    if (value instanceof Date) {
      result[key] = value.toISOString();
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      const first = value[0];
      // Drop arrays of objects (raw rational/IFD structs) — not useful as JSON
      if (typeof first === "object" && !(first instanceof Date)) continue;
      const sanitized = value
        .map((v) => {
          if (v instanceof Date) return v.toISOString();
          if (typeof v === "string") return sanitizeString(v);
          return v;
        })
        .filter((v): v is NonNullable<typeof v> => v !== null && v !== undefined);
      if (sanitized.length > 0) result[key] = sanitized;
      continue;
    }

    if (typeof value === "object") {
      const nested = sanitizeExif(value);
      if (Object.keys(nested).length > 0) result[key] = nested;
      continue;
    }

    // Number, boolean — keep as-is
    result[key] = value;
  }

  return result;
}

/**
 * Extract EXIF metadata and GPS coordinates from an image file.
 * Returns null fields for non-image files or files without EXIF data.
 * Never throws — upload always continues regardless of EXIF parse failures.
 */
export async function extractExifFromFile(file: File): Promise<ExifResult> {
  if (!IMAGE_MIME_TYPES.has(file.type.toLowerCase())) {
    return { exif_metadata: null, geometry: null };
  }

  // --- GPS extraction (exifr.gps converts DMS → decimal automatically) ---
  let geometry: ExifResult["geometry"] = null;
  try {
    const gps = await exifr.gps(file);
    if (
      gps &&
      typeof gps.latitude === "number" &&
      typeof gps.longitude === "number" &&
      Number.isFinite(gps.latitude) &&
      Number.isFinite(gps.longitude) &&
      // Reject null-island (0, 0) — almost always missing or corrupted data
      !(gps.longitude === 0 && gps.latitude === 0) &&
      gps.longitude >= -180 && gps.longitude <= 180 &&
      gps.latitude >= -90 && gps.latitude <= 90
    ) {
      // GeoJSON Point: [lng, lat] — matches `geometry` shape on Place/FamilyArea/Trip
      geometry = { type: "Point", coordinates: [gps.longitude, gps.latitude] };
    }
  } catch (gpsError) {
    console.warn(`[EXIF] GPS extraction failed for ${file.name}:`, gpsError);
  }

  // --- Full EXIF parse ---
  let exif_metadata: Record<string, any> | null = null;
  try {
    const raw = await exifr.parse(file, {
      gps: true,
      exif: true,
      iptc: true,
      xmp: false,
      icc: false,
      // Skip large binary blobs and fields known to contain control characters
      skip: ["MakerNote", "UserComment", "PrintImageMatching", "ThumbnailImage", "ApplicationRecordVersion"]
    });

    if (raw) {
      const sanitized = sanitizeExif(raw);
      if (Object.keys(sanitized).length > 0) {
        exif_metadata = sanitized;
      }
    }
  } catch (parseError) {
    console.warn(`[EXIF] Parse failed for ${file.name}:`, parseError);
  }

  return { exif_metadata, geometry };
}
